import { Trans, useTranslation } from 'react-i18next'

import {
  Alert,
  DangerButton,
  HStack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'

type Props = {
  isOpen: boolean
  /** 削除対象の下書き件数 (= design `#bid-clear-count`)。 */
  count: number
  isSubmitting: boolean
  errorMessage: string | undefined
  onClose: () => void
  /** 「全削除」= 下書き入札を一括削除する。 */
  onDelete: () => void
  /**
   * モーダル見出し。 省略時は通常入札の「入札の全削除」。
   * ブロック入札タブは design `spot.html` `#block-clear-modal` の
   * 「ブロック入札の全削除」を渡す (issue #2091)。
   */
  title?: string
  /** 件数を含む本文 1 文の i18n キー。 省略時は通常入札の文言。 件数は `<count>` で包み、 強調する。 */
  bodyKey?: string
  /**
   * 削除の対象外になった下書きの件数を伝える 1 行 (絞り込みで表示していない札など)。
   * 件数の下に出す。 対象外が無い時は渡さない。
   */
  excludedNote?: string
  /**
   * warning alert の文言。 省略時は通常入札の文言。 ブロック入札は
   * 「LINK / LOOP のペアも全 leg 一緒に削除される」注意を含む文言を渡す
   * (design `spot.html` `#block-clear-modal`)。
   */
  csvNote?: string
}

/**
 * 入札の全削除 確認モーダル (design `spot.html` `#bid-clear-modal` / `#block-clear-modal`、
 * 機能詳細7)。
 *
 * 削除対象は下書き (DRAFT) 状態の入札のみ。 確認ダイアログのボタンは
 * 「キャンセル」「全削除」の 2 つ。 削除前の控えは ⋯メニューの CSV ダウンロードで取る
 * (AC-BSP-136、 #12131)。
 * 通常入札 / ブロック入札で見出し・本文・注意文だけが変わるので、 文言を prop で
 * 差し替えられるようにして 1 コンポーネントを共用する (= design も同一パターン)。
 */
export const ClearAllBidsConfirmModal = ({
  isOpen,
  count,
  isSubmitting,
  errorMessage,
  onClose,
  onDelete,
  title,
  bodyKey = 'page:biddingSpot.clearAll.body',
  excludedNote,
  csvNote,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Modal
      /* footer のボタンは DS 既定の `md` (40px / 14px / paddingX 16px) に揃える
         (2026-09-02 デザイナー指摘)。ここだけ `sm` (32px / 12px / paddingX 12px) で、
         8080 の `#bid-clear-modal` の footer が素の `.btn` = 40px/14px/16px
         (`.btn-sm` は付いていない) なのとも、実装内の他の破壊的操作の確認モーダル
         7/8 件が DS 既定 md なのとも食い違っていた。 */
      footer={
        <HStack gap={3}>
          <TertiaryButton onClick={onClose} size="md">
            {t('common:action.cancel')}
          </TertiaryButton>
          <DangerButton loading={isSubmitting} onClick={onDelete} size="md">
            {t('page:biddingSpot.clearAll.delete')}
          </DangerButton>
        </HStack>
      }
      isOpen={isOpen}
      onClose={onClose}
      /* 8080 の `#bid-clear-modal` も `.ecl-modal--sm` を付けておらず 560px 側。
         ボタンを md にした時に幅を既定へ戻した経緯 (2026-09-02) を引き継ぐ。 */
      size="md"
      title={title ?? t('page:biddingSpot.clearAll.title')}
    >
      <VStack align="stretch" gap={3}>
        {errorMessage !== undefined && (
          <Alert status="error" title={errorMessage} />
        )}
        <Text>
          <Trans
            components={{ count: <Text as="strong" /> }}
            i18nKey={bodyKey}
            values={{ count }}
          />
        </Text>
        {excludedNote !== undefined && <Text>{excludedNote}</Text>}
        <Alert
          status="warning"
          title={csvNote ?? t('page:biddingSpot.clearAll.csvNote')}
        />
      </VStack>
    </Modal>
  )
}
